import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { PLATFORMS } from "@/lib/platforms";
import { toast } from "sonner";

const COLOURS = PLATFORMS.map((p) => p.hue);

export default function AliasEntry() {
  const { entryOpen, setEntryOpen, register, login } = useAuth();
  const [mode, setMode] = useState("new");
  const [alias, setAlias] = useState("");
  const [colour, setColour] = useState(COLOURS[1]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "new") {
        const u = await register({ alias, colour, email, password });
        toast(`Welcome, ${u.alias}. That name is all anyone will ever see.`);
      } else {
        const u = await login({ email, password });
        toast(`Back again, ${u.alias}.`);
      }
      setEntryOpen(false);
      setAlias(""); setEmail(""); setPassword("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
      <DialogContent className="bg-[var(--paper)] border-[var(--line)] max-w-md" data-testid="alias-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {mode === "new" ? "Pick a name that is not yours" : "Welcome back"}
          </DialogTitle>
          <DialogDescription className="text-[var(--ink-soft)]">
            {mode === "new"
              ? "An alias and a colour. Your email exists only so progress survives across devices. Nobody, including our specialists, ever sees who you are."
              : "Your alias, your colour, everything you saved. Right where you left it."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          {mode === "new" && (
            <>
              <div>
                <label className="kicker text-[var(--ink-soft)]">Your alias</label>
                <input
                  value={alias}
                  onChange={(e) => setAlias(e.target.value)}
                  required
                  minLength={2}
                  maxLength={24}
                  placeholder="Putnik, Sova, Reka, anything"
                  className="mt-1.5 w-full rounded-xl border border-[var(--line)] bg-white/60 px-4 py-2.5 outline-none focus:border-[var(--ink)] transition-colors"
                  data-testid="alias-input"
                />
              </div>
              <div>
                <label className="kicker text-[var(--ink-soft)]">Your colour</label>
                <div className="mt-2 flex gap-2.5" data-testid="colour-picker">
                  {COLOURS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColour(c)}
                      className="w-8 h-8 rounded-full transition-transform hover:scale-110"
                      style={{ background: c, outline: colour === c ? "2.5px solid var(--ink)" : "none", outlineOffset: "3px" }}
                      data-testid={`colour-${c.replace("#", "")}`}
                      aria-label={c}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
          <div>
            <label className="kicker text-[var(--ink-soft)]">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Only for saving progress"
              className="mt-1.5 w-full rounded-xl border border-[var(--line)] bg-white/60 px-4 py-2.5 outline-none focus:border-[var(--ink)] transition-colors"
              data-testid="email-input"
            />
          </div>
          <div>
            <label className="kicker text-[var(--ink-soft)]">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="At least six characters"
              className="mt-1.5 w-full rounded-xl border border-[var(--line)] bg-white/60 px-4 py-2.5 outline-none focus:border-[var(--ink)] transition-colors"
              data-testid="password-input"
            />
          </div>
          {error && <p className="text-sm text-[#D64533]" data-testid="alias-entry-error">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="btn-ink w-full rounded-full py-3 font-semibold disabled:opacity-50"
            data-testid="alias-entry-submit"
          >
            {busy ? "One moment" : mode === "new" ? "Step inside" : "Continue"}
          </button>
        </form>
        <button
          onClick={() => { setMode(mode === "new" ? "return" : "new"); setError(""); }}
          className="text-sm text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors mx-auto"
          data-testid="alias-entry-toggle"
        >
          {mode === "new" ? "Already carrying an alias? Return here" : "New here? Pick an alias instead"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
